/**
 * Map raw error strings to user-friendly messages.
 * Never expose requestIds, URLs, stack traces, or HTTP internals to users.
 */

const PATTERNS: [RegExp, string][] = [
  // Specific build errors from edge function — surface actual reason
  [/MODEL_INVALID_JSON/i, "Couldn't apply that change — click Retry to try again."],
  [/EMPTY_PATCH/i, "The AI returned no code changes. Click 'Retry last' or try a different prompt."],
  [/not under allowed prefixes|OUT_OF_SCOPE/i, "The AI tried to edit a restricted file. Click 'Retry last' to try again."],
  [/Plan invalid|PATCH_GUARD/i, "The build plan had an issue. Click 'Retry last' to try again."],
  [/All \d+ items rejected/i, "The AI's edits were all rejected by safety checks. Click 'Retry last' to try again."],
  [/Cannot delete critical file/i, "The AI tried to delete a protected file. Click 'Retry last' to try again."],
  [/Could not find.*files|couldn't locate|couldn't find.*element/i, "I couldn't find the right place to make that change. Try being more specific about which file or component."],
  [/JSON.*parse|Unexpected token|Unexpected end of JSON/i, "Couldn't apply that change — click Retry to try again."],
  [/context.?length|too.?long|max.?tokens/i, "The project is too large for a single request. Try a simpler prompt."],
  // Network / auth / rate limit
  [/HTTP 429|rate.?limit|too many requests/i, "Too many requests. Please wait a moment and try again."],
  [/Failed to fetch|Load failed|NetworkError|ERR_NETWORK|ECONNREFUSED|fetch failed/i, "Network connection issue. Check your internet and try again."],
  [/timeout|ETIMEDOUT|timed? ?out/i, "The request took too long. Click 'Retry last' to try again."],
  [/Connection was interrupted/i, "The request was interrupted. Please try again."],
  [/HTTP 401|Unauthorized|auth.*expired|session.*expired/i, "Your session expired. Please sign in again."],
  [/HTTP 403|Forbidden/i, "You don't have permission for this action. Please sign in again."],
  // 502/504 Bad Gateway / Gateway Timeout — infrastructure issues, not user's fault
  [/HTTP 502|Bad Gateway/i, "Server temporarily unavailable. Please try again in a moment."],
  [/HTTP 504|Gateway Timeout/i, "Server took too long to respond. Please try again."],
  // Generic 5xx LAST — only if no specific pattern matched above. Sanitize & show the real reason.
  [/HTTP 5\d\d|Internal Server Error|INTERNAL_ERROR/i, null as unknown as string],
];

/** Strip technical noise (requestIds, URLs, HTTP details) from an error for user display. */
function sanitize(raw: string): string {
  return raw
    .replace(/requestId=[^\s]*/gi, "")
    .replace(/url=[^\s]*/gi, "")
    .replace(/https?:\/\/[^\s]*/gi, "")
    .replace(/Edge Function HTTP \d+\s*/gi, "")
    .replace(/\s*\|\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function friendlyErrorMessage(raw: unknown): string {
  const str = typeof raw === "string" ? raw : raw instanceof Error ? raw.message : String(raw ?? "");
  if (!str.trim()) return "Something went wrong. Please try again.";

  for (const [pattern, friendly] of PATTERNS) {
    if (pattern.test(str)) {
      // null sentinel = skip canned message, fall through to sanitize path
      if (friendly !== null) return friendly;
      break;
    }
  }

  // Fallback: strip technical noise but show the actual error reason
  const clean = sanitize(str);
  const snippet = clean.length > 120 ? clean.slice(0, 120) + "..." : clean;
  return snippet
    ? `${snippet} — Click 'Retry last' or try a different prompt.`
    : "Something went wrong. Click 'Retry last' or try a different prompt.";
}
